import { Controller } from "@hotwired/stimulus"
import { getAllJSON, sendJSON, formErrorMessage } from "../api.js"
import { setComboOptions, comboValue, clearCombo } from "../combobox_helpers.js"

// Kontroler deklaracji zapotrzebowania klienta.
//
// connect(): ładuje kategorie (filtrowane pionem) i województwa do comboboxów;
//            pobiera listę własnych deklaracji organizacji.
// County: combobox zdalny z depends_on="voivodeship" — zarządza się sam, nie ładujemy ręcznie.
// submit(): buduje payload; puste counties = tablice [] = całe województwo (reguła domenowa).
// cancel(): prosi o potwierdzenie (destructive) przed POST /cancel/.
//
// Wymagania szablonu:
//   data-demand-vertical-id-value="{{ vertical.pk }}" — PK wertykału do payload POST.
//   Aktualny szablon ma tylko data-demand-vertical-value="{{ vertical.slug }}" (slug do URL).
//   Do czasu poprawki szablonu PK nie dotrze do kontrolera — patrz TODO poniżej.
// TODO(demand#4): szablon demand.html potrzebuje data-demand-vertical-id-value="{{ vertical.pk }}".
//   Implementer-frontend lane — nie dotykaj stąd szablonu.
//
// Zasady:
//   - Zero innerHTML — wszystkie węzły przez createElement + textContent.
//   - Puste counties[] = cały wybór = całe województwo; nigdy null ani pominięte pole.
//   - sendJSON nie rzuca; brak optimistic-UI (fake success jest zakazany).

export default class extends Controller {
  static targets = [
    "categorySelect",
    "voivSelect",
    "countySelect",
    "quantityInput",
    "dailyQuantityInput",
    "notesInput",
    "submitBtn",
    "formError",
    "rows",
    "status",
  ]

  static values = {
    url: String,
    categoriesUrl: String,
    voivodeshipsUrl: String,
    countiesUrl: String,
    vertical: String,    // slug — używany do filtrowania kategorii (?vertical=slug)
    verticalId: Number,  // PK — wysyłany w payload POST (wymagane w szablonie)
  }

  // Mapy id → name do rozwijania kluczy FK w wierszach listy.
  #categoryById = new Map()
  #voivById = new Map()
  #countyById = new Map()

  async connect() {
    // Słowniki i lista deklaracji ładowane równolegle; lista czeka na słowniki (nazwy w wierszach).
    await Promise.all([
      this.#loadCategories(),
      this.#loadVoivodeships(),
    ])
    await this.#loadOrders()
  }

  async submit(event) {
    event?.preventDefault()
    this.#clearFormError()

    const category = comboValue(this.categorySelectTarget)
    const voivodeship = comboValue(this.voivSelectTarget)
    const quantity = this.quantityInputTarget.value.trim()

    if (!category) {
      this.#showFormError("Wybierz kategorię.")
      return
    }
    if (!voivodeship) {
      this.#showFormError("Wybierz województwo.")
      return
    }
    if (!quantity || Number(quantity) < 1) {
      this.#showFormError("Podaj liczbę leadów miesięcznie (min. 1).")
      return
    }

    // Puste counties = tablica [] = całe województwo (reguła domenowa, nie brak danych).
    // Nigdy null ani pominięcie pola — serializer ma default=list, ale wysyłamy jawnie.
    const countiesRaw = comboValue(this.countySelectTarget)
    const counties = (Array.isArray(countiesRaw) ? countiesRaw : []).map(Number)

    const payload = {
      vertical: this.verticalIdValue,
      category: Number(category),
      voivodeship: Number(voivodeship),
      counties,
      monthly_quantity: Number(quantity),
    }
    const dailyQty = this.dailyQuantityInputTarget.value.trim()
    if (dailyQty && Number(dailyQty) >= 1) payload.daily_quantity = Number(dailyQty)
    const notes = this.notesInputTarget.value.trim()
    if (notes) payload.notes = notes

    this.submitBtnTarget.disabled = true
    const { ok, status, data } = await sendJSON("POST", this.urlValue, payload)
    this.submitBtnTarget.disabled = false

    if (ok && status === 201) {
      await this.#resetForm()
      await this.#loadOrders()
    } else {
      this.#showFormError(this.#extractError(data))
    }
  }

  // Anulowanie jest operacją destrukcyjną (brak edycji w v1) — zawsze z potwierdzeniem.
  async cancel({ currentTarget }) {
    const id = currentTarget.dataset.demandOrderIdParam
    if (!window.confirm("Anulować tę deklarację? Nie można jej cofnąć ani edytować.")) return

    const { ok, data } = await sendJSON("POST", `${this.urlValue}${id}/cancel/`, undefined)
    if (ok) {
      await this.#loadOrders()
    } else {
      this.#showStatus(this.#extractError(data))
    }
  }

  // --- private ---

  async #loadCategories() {
    let items
    try {
      items = await getAllJSON(`${this.categoriesUrlValue}?vertical=${this.verticalValue}`)
    } catch {
      return
    }
    this.#categoryById.clear()
    for (const c of items) {
      this.#categoryById.set(c.id, c.name)
    }
    const opts = items.map(c => ({ value: c.id, label: c.name }))
    await setComboOptions(this.application, this.categorySelectTarget, opts)
  }

  async #loadVoivodeships() {
    let items
    try {
      items = await getAllJSON(this.voivodeshipsUrlValue)
    } catch {
      return
    }
    this.#voivById.clear()
    for (const v of items) {
      this.#voivById.set(v.id, v.name)
    }
    const opts = items.map(v => ({ value: v.id, label: v.name }))
    await setComboOptions(this.application, this.voivSelectTarget, opts)
  }

  async #loadOrders() {
    let items
    try {
      items = await getAllJSON(`${this.urlValue}?vertical=${encodeURIComponent(this.verticalValue)}`)
    } catch {
      this.#showStatus("Nie udało się załadować deklaracji.")
      return
    }

    // Załaduj nazwy powiatów dla deklaracji z powiatami (lazy, per voivodeship).
    await this.#ensureCountyNames(items)

    this.rowsTarget.textContent = ""

    if (!items.length) {
      this.statusTarget.hidden = false
      this.statusTarget.textContent = "Brak deklaracji."
      return
    }

    this.statusTarget.hidden = true

    const tpl = document.getElementById("demand-row-template")
    for (const order of items) {
      const row = tpl.content.cloneNode(true).firstElementChild

      row.querySelector('[data-field="category"]').textContent =
        this.#categoryById.get(order.category) ?? (order.category_name || "—")

      row.querySelector('[data-field="voiv"]').textContent =
        this.#voivById.get(order.voivodeship) ?? String(order.voivodeship)

      const countiesEl = row.querySelector('[data-field="counties"]')
      if (!order.counties.length) {
        countiesEl.textContent = "całe województwo"
      } else {
        countiesEl.textContent = order.counties
          .map((id) => this.#countyById.get(id) ?? String(id))
          .join(", ")
      }

      row.querySelector('[data-field="quantity"]').textContent = String(order.monthly_quantity)
      row.querySelector('[data-field="dailyQuantity"]').textContent =
        order.daily_quantity != null ? String(order.daily_quantity) : '—'

      const badge = row.querySelector('[data-field="badge"]')
      if (order.status === "active") {
        badge.textContent = "aktywna"
        badge.classList.add("ls-badge--sent")
      } else {
        badge.textContent = "anulowana"
        badge.classList.add("ls-badge--new")
      }

      const cancelBtn = row.querySelector('[data-field="cancelBtn"]')
      if (order.status === "active") {
        // Stimulus params: data-demand-order-id-param przesyłany do cancel({ currentTarget })
        cancelBtn.setAttribute("data-demand-order-id-param", String(order.id))
      } else {
        cancelBtn.hidden = true
      }

      this.rowsTarget.appendChild(row)
    }
  }

  // Pobiera nazwy powiatów dla deklaracji, których powiaty nie są jeszcze w cache.
  // Per voivodeship, żeby nie duplikować żądań (np. dwie deklaracje z tym samym woj.).
  async #ensureCountyNames(orders) {
    const voivIdsToFetch = new Set()
    for (const o of orders) {
      for (const cid of o.counties) {
        if (!this.#countyById.has(cid)) {
          voivIdsToFetch.add(o.voivodeship)
          break
        }
      }
    }
    await Promise.all(
      [...voivIdsToFetch].map(async (vid) => {
        try {
          const counties = await getAllJSON(`${this.countiesUrlValue}?voivodeship=${vid}`)
          for (const c of counties) this.#countyById.set(c.id, c.name)
        } catch {
          // Best-effort — nazwy powiatów degradują do PK, nie blokują renderowania.
        }
      }),
    )
  }

  async #resetForm() {
    await Promise.all([
      clearCombo(this.application, this.categorySelectTarget),
      clearCombo(this.application, this.voivSelectTarget),
      clearCombo(this.application, this.countySelectTarget),
    ])
    this.quantityInputTarget.value = ""
    this.dailyQuantityInputTarget.value = ""
    this.notesInputTarget.value = ""
    this.#clearFormError()
  }

  #extractError(data) {
    return formErrorMessage(data, this.element)
  }

  #clearFormError() {
    this.formErrorTarget.hidden = true
    this.formErrorTarget.textContent = ""
  }

  #showFormError(message) {
    this.formErrorTarget.hidden = false
    this.formErrorTarget.textContent = ""
    const span = document.createElement("span")
    span.textContent = message
    this.formErrorTarget.appendChild(span)
  }

  #showStatus(message) {
    this.statusTarget.hidden = false
    this.statusTarget.textContent = message
  }
}
